import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Typography, TextField, Button, IconButton, Stack, Paper,
  Chip, Avatar, Divider, Fade, Dialog, Alert, InputAdornment,
  Tooltip, LinearProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  TrackChanges as TrackerIcon,
  Link as LinkIcon,
  NotificationsActive as NotifIcon,
  YouTube as YouTubeIcon,
  MusicVideo as TikTokIcon,
  Instagram as InstagramIcon,
  ImageOutlined as PhotoIcon,
  AutoAwesome as MagicIcon,
  TableChart as TableChartIcon,
  UploadFile as UploadFileIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  AutoFixHigh as AutoIcon,
} from '@mui/icons-material';
import { toast } from 'sonner';
import useIsDesktop from '../../Hooks/useIsDesktop';
import UploadFileDialog from '../../Components/tools/uploadfile';
import { isValidVideoUrl } from '../../Utils/constants';

/* ────────────────────────────────────────────────────────────────── */
/* Z-index constants                                                  */
/* ────────────────────────────────────────────────────────────────── */

// Our full-screen overlay sits at MUI modal (1300) + 1 = 1301.
// Any dialog that needs to appear ON TOP of that overlay must be above 1301.
// We use 1402 as a safe, explicit number that beats any custom theme config.
const OVERLAY_ZINDEX  = 1301;
const DIALOG_ZINDEX   = 1402;

/* ────────────────────────────────────────────────────────────────── */
/* Constants                                                          */
/* ────────────────────────────────────────────────────────────────── */

const MAX_LINKS = 50;

const DUMMY_PHOTOS = [
  'https://storage.googleapis.com/blitzccmc.appspot.com/1/287262.jpg',
  'https://storage.googleapis.com/blitzccmc.appspot.com/1/wp2468668.jpg',
  'https://storage.googleapis.com/blitzccmc.appspot.com/1/eev9l01j44ab1.jpg',
];

/* ────────────────────────────────────────────────────────────────── */
/* URL / file helpers                                                 */
/* ────────────────────────────────────────────────────────────────── */

function isValidTrackerUrl(url) {
  if (!url) return false;
  try {
    const host = new URL(url).hostname.replace('www.', '');
    return ['youtube.com','youtu.be','tiktok.com','instagram.com'].includes(host);
  } catch { return false; }
}

function detectPlatform(url) {
  if (!url) return null;
  const l = url.toLowerCase();
  if (l.includes('youtube.com') || l.includes('youtu.be')) return 'youtube';
  if (l.includes('tiktok.com'))    return 'tiktok';
  if (l.includes('instagram.com')) return 'instagram';
  return null;
}

/** UploadFileDialog returns { file_url, url, ... } or a plain string */
function extractUrl(fileOrUrl) {
  if (!fileOrUrl) return '';
  if (typeof fileOrUrl === 'string') return fileOrUrl;
  return fileOrUrl.file_url || fileOrUrl.url || '';
}

function suggestNameFromUrl(url) {
  if (!url) return '';
  try {
    const u     = new URL(url);
    const host  = u.hostname.replace('www.', '');
    const parts = u.pathname.split('/').filter(Boolean);
    const titleCase = s =>
      s.replace(/_/g,' ').replace(/-/g,' ')
       .split(' ')
       .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
       .join(' ');

    if (host === 'tiktok.com') {
      const at = parts.find(p => p.startsWith('@'));
      if (at) return `${titleCase(at.slice(1))} Tracker`;
      if (parts[0]) return `${titleCase(parts[0])} Tracker`;
      return 'TikTok Tracker';
    }
    if (host === 'youtube.com' || host === 'youtu.be') {
      const at = parts.find(p => p.startsWith('@'));
      if (at) return `${titleCase(at.slice(1))} Tracker`;
      const cIdx = parts.indexOf('c');
      if (cIdx !== -1 && parts[cIdx+1]) return `${titleCase(parts[cIdx+1])} Tracker`;
      return 'YouTube Tracker';
    }
    if (host === 'instagram.com') {
      if (parts[0] === 'reel' || parts[0] === 'reels') return 'Instagram Reel';
      if (parts[0] === 'p') return 'Instagram Post';
      const at = parts.find(p => p.startsWith('@'));
      if (at) return `${titleCase(at.slice(1))} Tracker`;
      if (parts[0] && !['stories','tv','explore'].includes(parts[0])) return `${titleCase(parts[0])} Tracker`;
      return 'Instagram Tracker';
    }
  } catch { /* fall through */ }
  return '';
}

/* ────────────────────────────────────────────────────────────────── */
/* PlatformAvatar                                                     */
/* ────────────────────────────────────────────────────────────────── */

function PlatformAvatar({ url, index }) {
  const theme  = useTheme();
  const hasVal = !!url;
  const ok     = isValidTrackerUrl(url) || !url;
  const plat   = detectPlatform(url);
  const bgMap  = {
    youtube:   'linear-gradient(135deg,#ff0000,#cc0000)',
    tiktok:    'linear-gradient(135deg,#010101,#69C9D0)',
    instagram: 'linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)',
  };
  const bg = !ok && hasVal
    ? `linear-gradient(135deg,${theme.palette.error.light},${theme.palette.error.main})`
    : plat ? bgMap[plat]
    : `linear-gradient(135deg,${alpha(theme.palette.primary.main,0.15)},${alpha(theme.palette.secondary.main,0.15)})`;
  const icon = !ok && hasVal
    ? <LinkIcon sx={{ fontSize:20, color:'white' }}/>
    : plat === 'youtube'   ? <YouTubeIcon   sx={{ fontSize:20, color:'white' }}/>
    : plat === 'tiktok'    ? <TikTokIcon    sx={{ fontSize:20, color:'white' }}/>
    : plat === 'instagram' ? <InstagramIcon sx={{ fontSize:20, color:'white' }}/>
    : <Typography variant="body2" sx={{ color:theme.palette.text.disabled, fontWeight:700 }}>{index+1}</Typography>;
  return <Avatar sx={{ background:bg, width:44, height:44, flexShrink:0 }}>{icon}</Avatar>;
}

/* ────────────────────────────────────────────────────────────────── */
/* CSV helpers                                                        */
/* ────────────────────────────────────────────────────────────────── */

function splitCSVLine(line) {
  const result=[]; let cur='', inQ=false;
  for (const ch of line) {
    if (ch==='"') { inQ=!inQ; continue; }
    if (ch===',' && !inQ) { result.push(cur.trim()); cur=''; continue; }
    cur+=ch;
  }
  result.push(cur.trim()); return result;
}

function parseLinksCSV(text) {
  const lines = text.split(/\r?\n/).filter(l=>l.trim());
  if (lines.length<2) return { links:[], errors:['CSV needs a header row and at least one data row.'] };
  const headers = splitCSVLine(lines[0]).map(h=>h.toLowerCase().replace(/['"]/g,'').trim());
  const linkIdx = headers.findIndex(h=>['url','link','video','content'].some(k=>h.includes(k)));
  if (linkIdx===-1) return { links:[], errors:['Missing "url" or "link" column.'] };
  const links=[], errors=[];
  lines.slice(1).forEach((line,i) => {
    if (!line.trim()) return;
    const url = (splitCSVLine(line)[linkIdx]||'').replace(/^"|"$/g,'').trim();
    if (!url) return;
    if (isValidTrackerUrl(url)) links.push(url);
    else errors.push(`Row ${i+2}: not a valid YouTube/TikTok/Instagram URL.`);
  });
  return { links, errors };
}

/* ────────────────────────────────────────────────────────────────── */
/* LinkCSVModal                                                       */
/* ────────────────────────────────────────────────────────────────── */

function LinkCSVModal({ open, onClose, onImport }) {
  const theme   = useTheme();
  const fileRef = useRef(null);
  const [drag,        setDrag]        = useState(false);
  const [parseResult, setParseResult] = useState(null);
  const [fileName,    setFileName]    = useState('');

  const handleFile = useCallback((file) => {
    if (!file) return;
    if (!file.name.match(/\.csv$/i)) { toast.error('Please upload a .csv file'); return; }
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = e => setParseResult(parseLinksCSV(e.target.result));
    reader.readAsText(file);
  }, []);

  const reset = () => { setParseResult(null); setFileName(''); };

  const handleConfirm = () => {
    if (!parseResult?.links?.length) return;
    onImport(parseResult.links);
    reset(); onClose();
  };

  const downloadTemplate = () => {
    const csv = 'url\nhttps://www.youtube.com/watch?v=dQw4w9WgXcQ\nhttps://www.tiktok.com/@creator/video/123\nhttps://www.instagram.com/p/ABCDEF/\n';
    Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([csv], { type:'text/csv' })),
      download: 'tracker_links_template.csv',
    }).click();
  };

  return (
    <Dialog
      open={open}
      onClose={() => { reset(); onClose(); }}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
      /**
       * FIX: use React's `style` prop (applied as inline styles) rather than
       * `sx` (applied as CSS classes). Inline styles always beat CSS classes,
       * including any z-index MUI sets internally on the Modal root element.
       *
       * DIALOG_ZINDEX = 1402, which is safely above:
       *   - MUI default modal z-index (1300)
       *   - Our full-screen overlay (OVERLAY_ZINDEX = 1301)
       */
      style={{ zIndex: DIALOG_ZINDEX }}
    >
      <DialogTitle sx={{ p:0 }}>
        <Box sx={{ px:3, py:2, display:'flex', alignItems:'center', justifyContent:'space-between', borderBottom:1, borderColor:'divider', background:`linear-gradient(135deg,${alpha(theme.palette.info.main,0.06)} 0%,white 100%)` }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
            <Box sx={{ width:36, height:36, borderRadius:2, bgcolor:alpha(theme.palette.info.main,0.12), display:'flex', alignItems:'center', justifyContent:'center' }}>
              <TableChartIcon sx={{ color:'info.main', fontSize:20 }}/>
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={800} fontSize="1rem" lineHeight={1.2}>Import Links from CSV</Typography>
              <Typography variant="caption" color="text.secondary">{fileName||'Bulk-add YouTube, TikTok & Instagram URLs'}</Typography>
            </Box>
          </Box>
          <Box sx={{ display:'flex', gap:1 }}>
            <Button size="small" startIcon={<DownloadIcon sx={{fontSize:14}}/>} onClick={downloadTemplate} sx={{ fontSize:'0.75rem', textTransform:'none', fontWeight:700 }}>Template</Button>
            <IconButton size="small" onClick={()=>{reset();onClose();}}><CloseIcon fontSize="small"/></IconButton>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p:0 }}>
        {!parseResult ? (
          <Box sx={{ p:3 }}>
            <Box
              onDragOver={e=>{e.preventDefault();setDrag(true);}}
              onDragLeave={()=>setDrag(false)}
              onDrop={e=>{e.preventDefault();setDrag(false);handleFile(e.dataTransfer.files[0]);}}
              onClick={()=>fileRef.current?.click()}
              sx={{
                border:'2px dashed',
                borderColor:drag?'info.main':alpha(theme.palette.info.main,0.35),
                borderRadius:3, p:6, textAlign:'center', cursor:'pointer',
                bgcolor:drag?alpha(theme.palette.info.main,0.05):alpha(theme.palette.info.main,0.02),
                transition:'all 0.2s',
                '&:hover':{ borderColor:'info.main', bgcolor:alpha(theme.palette.info.main,0.04) },
              }}
            >
              <input ref={fileRef} type="file" accept=".csv,text/csv" hidden onChange={e=>handleFile(e.target.files[0])}/>
              <Box sx={{ width:56, height:56, borderRadius:2.5, bgcolor:alpha(theme.palette.info.main,0.1), display:'flex', alignItems:'center', justifyContent:'center', mx:'auto', mb:2, transform:drag?'scale(1.1) rotate(-4deg)':'none', transition:'transform 0.2s' }}>
                <UploadFileIcon sx={{ fontSize:28, color:'info.main' }}/>
              </Box>
              <Typography variant="h6" fontWeight={700} mb={0.5} fontSize="1rem">{drag?'Drop it!':'Drop your CSV here'}</Typography>
              <Typography variant="body2" color="text.secondary" mb={2.5}>
                or <Box component="span" sx={{ color:'info.main', fontWeight:700 }}>click to browse</Box>
              </Typography>
              <Box sx={{ p:1.5, borderRadius:2, bgcolor:'grey.50', border:'1px solid', borderColor:'grey.200', display:'inline-block', textAlign:'left' }}>
                <Typography variant="caption" sx={{ fontWeight:700, color:'text.secondary', display:'block', mb:0.5, textTransform:'uppercase', fontSize:'0.65rem', letterSpacing:'0.08em' }}>One column needed</Typography>
                <Chip label="url" size="small" sx={{ fontSize:'0.72rem', height:22, bgcolor:'white', fontWeight:700, fontFamily:'monospace', border:'1px solid', borderColor:'grey.300' }}/>
                <Typography variant="caption" color="text.secondary" sx={{ display:'block', mt:0.75 }}>YouTube, TikTok, or Instagram URLs — one per row</Typography>
              </Box>
            </Box>
          </Box>
        ) : (
          <Box sx={{ p:3 }}>
            <Box sx={{ display:'flex', gap:1.5, mb:2.5 }}>
              <Box sx={{ flex:1, p:1.5, borderRadius:2, bgcolor:'success.50', border:'1px solid', borderColor:'success.200', textAlign:'center' }}>
                <Typography variant="h5" fontWeight={900} color="success.main" lineHeight={1}>{parseResult.links.length}</Typography>
                <Typography variant="caption" color="success.dark" fontWeight={700}>Valid links</Typography>
              </Box>
              {parseResult.errors.length>0 && (
                <Box sx={{ flex:1, p:1.5, borderRadius:2, bgcolor:'error.50', border:'1px solid', borderColor:'error.200', textAlign:'center' }}>
                  <Typography variant="h5" fontWeight={900} color="error.main" lineHeight={1}>{parseResult.errors.length}</Typography>
                  <Typography variant="caption" color="error.dark" fontWeight={700}>Skipped</Typography>
                </Box>
              )}
            </Box>
            {parseResult.errors.length>0 && (
              <Alert severity="warning" sx={{ mb:2, borderRadius:2 }}>
                {parseResult.errors.slice(0,3).map((e,i)=><Typography key={i} variant="caption" sx={{display:'block'}}>• {e}</Typography>)}
                {parseResult.errors.length>3 && <Typography variant="caption">…and {parseResult.errors.length-3} more</Typography>}
              </Alert>
            )}
            {parseResult.links.length>0 && (
              <TableContainer component={Paper} elevation={0} sx={{ border:'1px solid', borderColor:'grey.200', borderRadius:2, maxHeight:280 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {['Platform','URL'].map(h=>(
                        <TableCell key={h} sx={{ fontWeight:800, fontSize:'0.7rem', textTransform:'uppercase', letterSpacing:'0.06em', bgcolor:'grey.50', color:'text.secondary' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parseResult.links.map((url,i)=>{
                      const plat=detectPlatform(url);
                      return (
                        <TableRow key={i}>
                          <TableCell>
                            <Chip label={plat||'?'} size="small"
                              icon={plat==='youtube'?<YouTubeIcon sx={{fontSize:'11px !important'}}/>:plat==='instagram'?<InstagramIcon sx={{fontSize:'11px !important'}}/>:<TikTokIcon sx={{fontSize:'11px !important'}}/>}
                              color={plat==='youtube'?'error':plat==='instagram'?'secondary':'default'}
                              variant="outlined" sx={{ fontSize:'0.65rem', height:18, textTransform:'capitalize' }}/>
                          </TableCell>
                          <TableCell sx={{ fontSize:'0.78rem', color:'text.secondary', maxWidth:280, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{url}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            <Box sx={{ mt:1.5, textAlign:'center' }}>
              <Button size="small" variant="text" startIcon={<UploadFileIcon sx={{fontSize:14}}/>} onClick={reset} sx={{ fontSize:'0.78rem', textTransform:'none', color:'text.secondary' }}>
                Upload a different file
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px:3, py:2, borderTop:'1px solid', borderColor:'grey.200', gap:1 }}>
        <Button onClick={()=>{reset();onClose();}} variant="outlined" sx={{ borderRadius:2, textTransform:'none', fontWeight:700 }}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained" disabled={!parseResult?.links?.length}
          sx={{ borderRadius:2, textTransform:'none', fontWeight:700, minWidth:180 }}>
          {parseResult?.links?.length ? `Add ${parseResult.links.length} link${parseResult.links.length!==1?'s':''} →` : 'No valid links found'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* NewTracker                                                         */
/* ────────────────────────────────────────────────────────────────── */

export function NewTracker({ open, handleClose, handleAddTracker }) {
  const theme     = useTheme();
  const isDesktop = useIsDesktop();

  const [links,            setLinks]            = useState(['']);
  const [name,             setName]             = useState('');
  const [nameTouched,      setNameTouched]      = useState(false);
  const [description,      setDescription]      = useState('');
  const [trackerPhoto,     setTrackerPhoto]      = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [csvModalOpen,     setCSVModalOpen]      = useState(false);
  const [freq,             setFreq]             = useState('2');

  // Lock body scroll on desktop
  useEffect(() => {
    if (!isDesktop || !open) return;
    const scrollY = window.scrollY;
    document.body.style.cssText = `overflow:hidden;position:fixed;top:-${scrollY}px;left:0;right:0;`;
    return () => { document.body.style.cssText=''; window.scrollTo(0, scrollY); };
  }, [open, isDesktop]);

  const cleanup = () => {
    setLinks(['']); setName(''); setNameTouched(false);
    setDescription(''); setTrackerPhoto(''); setFreq('2');
  };
  useEffect(() => { if (open) cleanup(); }, [open]);

  // Auto-suggest name from first valid link
  useEffect(() => {
    if (nameTouched) return;
    const first = links.find(l => l && isValidTrackerUrl(l));
    if (first) { const s = suggestNameFromUrl(first); if (s) setName(s); }
  }, [links, nameTouched]);

  const handleFreq = e => {
    if (e.target.valueAsNumber < 2) e.target.setCustomValidity('Only Blitz Campaigns support daily notifications');
    else e.target.setCustomValidity('');
    setFreq(e.target.value);
  };

  const handleLink = (e, idx) => {
    const val = e.target.value;
    setLinks(prev => { const u=[...prev]; u[idx]=val; return u; });
    if (!isValidTrackerUrl(val) && val) e.target.setCustomValidity('Not a valid YouTube, TikTok, or Instagram link');
    else e.target.setCustomValidity('');
  };

  const handleCSVImport = (importedLinks) => {
    setLinks(prev => {
      const existing = prev.filter(l=>l);
      const merged   = [...new Set([...existing,...importedLinks])].slice(0,MAX_LINKS);
      return merged.length<MAX_LINKS ? [...merged,''] : merged;
    });
    toast.success(`${importedLinks.length} link${importedLinks.length!==1?'s':''} imported`);
  };

  const handlePhotoUploadSuccess = (fileOrUrl) => {
    const url = extractUrl(fileOrUrl);
    if (!url) { toast.error('Could not read uploaded file URL. Please try again.'); return; }
    setTrackerPhoto(url);
    setUploadDialogOpen(false);
  };

  const handleCreate = () => {
    const finalPhoto = trackerPhoto || DUMMY_PHOTOS[Math.floor(Math.random()*DUMMY_PHOTOS.length)];
    try {
      handleAddTracker({
        name, description,
        notif_freq: parseInt(freq),
        links: links.filter(l=>l&&isValidTrackerUrl(l)).map(link=>({link,notes:''})),
        tracker_photo: finalPhoto,
      });
      toast.success('Tracker created!', { description:`${name} • ${validLinks.length} link${validLinks.length!==1?'s':''}` });
      cleanup();
    } catch (err) { alert(err); }
  };

  const validLinks = links.filter(l=>l&&isValidTrackerUrl(l));
  const canSubmit  = name.trim().length>0 && validLinks.length>0;
  const progress   = Math.min((validLinks.length/MAX_LINKS)*100, 100);
  const autoNamed  = !nameTouched && name.length>0;

  /* ── Mobile ─────────────────────────────────────────────────────── */

  if (!isDesktop) {
    return (
      <>
        <Dialog open={open} onClose={handleClose} fullScreen>
          <Box sx={{ display:'flex', flexDirection:'column', height:'100%' }}>
            <Box sx={{ px:2, py:1.5, borderBottom:1, borderColor:'divider', display:'flex', alignItems:'center', justifyContent:'space-between', bgcolor:'background.paper' }}>
              <Box sx={{ display:'flex', alignItems:'center', gap:1.5 }}>
                <Box sx={{ width:36, height:36, borderRadius:2, background:'linear-gradient(135deg,#4facfe 0%,#00f2fe 100%)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                  <TrackerIcon sx={{ color:'white', fontSize:20 }}/>
                </Box>
                <Typography variant="h6" fontWeight={700}>New Tracker</Typography>
              </Box>
              <Box sx={{ display:'flex', gap:1 }}>
                <Button onClick={handleClose} color="inherit" size="small">Cancel</Button>
                <Button variant="contained" size="small" disabled={!canSubmit} onClick={handleCreate} sx={{ borderRadius:2, fontWeight:700 }}>Create</Button>
              </Box>
            </Box>
            <Box sx={{ flex:1, overflowY:'auto', p:2 }}>
              <Stack spacing={2.5}>
                <TextField fullWidth label="Tracker Name" value={name} required
                  onChange={e => { setName(e.target.value); setNameTouched(true); }}
                  InputProps={{ endAdornment: autoNamed ? <Tooltip title="Auto-suggested from your first link" arrow><AutoIcon sx={{ fontSize:18, color:'info.main', cursor:'default' }}/></Tooltip> : undefined }}/>
                <TextField fullWidth label="Description" value={description} onChange={e=>setDescription(e.target.value)} multiline rows={2}/>
                <TextField type="number" label="Notification Frequency" value={freq} onChange={handleFreq} inputProps={{min:1}} error={parseInt(freq)<2}
                  InputProps={{ endAdornment:<InputAdornment position="end">days</InputAdornment> }}
                  helperText={parseInt(freq)>1?`Email every ${freq} days`:'Daily = use Blitz Campaign'}/>
                <Button variant="outlined" startIcon={trackerPhoto?<EditIcon/>:<PhotoIcon/>} onClick={()=>setUploadDialogOpen(true)} sx={{borderRadius:2}}>
                  {trackerPhoto?'Change Cover Photo':'Add Cover Photo'}
                </Button>
                {trackerPhoto && <Box component="img" src={trackerPhoto} sx={{ width:'100%', height:120, objectFit:'cover', borderRadius:2 }}/>}
                <Divider/>
                <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                  <Typography variant="subtitle1" fontWeight={600}>Links ({validLinks.length})</Typography>
                  <Button size="small" variant="outlined" startIcon={<TableChartIcon/>} onClick={()=>setCSVModalOpen(true)} sx={{ borderRadius:2, fontSize:'0.75rem' }}>Import CSV</Button>
                </Box>
                {links.map((link,i)=>(
                  <Box key={i} sx={{ display:'flex', gap:1, alignItems:'flex-start' }}>
                    <PlatformAvatar url={link} index={i}/>
                    <TextField fullWidth size="small" label={`Link ${i+1}`} value={link} required onChange={e=>handleLink(e,i)}
                      error={!!link&&!isValidTrackerUrl(link)} helperText={!!link&&!isValidTrackerUrl(link)?'YouTube, TikTok, or Instagram URL only':''}/>
                    <IconButton disabled={links.length<=1} size="small" onClick={()=>setLinks(p=>p.filter((_,idx)=>idx!==i))} sx={{mt:0.5,color:'error.main'}}>
                      <DeleteIcon fontSize="small"/>
                    </IconButton>
                  </Box>
                ))}
                {links.length<MAX_LINKS && <Button variant="outlined" startIcon={<AddIcon/>} onClick={()=>setLinks(p=>[...p,''])} sx={{borderRadius:2}}>Add Link</Button>}
              </Stack>
            </Box>
          </Box>
        </Dialog>
        {/* Rendered outside fullScreen Dialog; style prop ensures z-index beats everything */}
        <UploadFileDialog open={uploadDialogOpen} onClose={()=>setUploadDialogOpen(false)} onUploadSuccess={handlePhotoUploadSuccess}/>
        <LinkCSVModal     open={csvModalOpen}     onClose={()=>setCSVModalOpen(false)}     onImport={handleCSVImport}/>
      </>
    );
  }

  /* ── Desktop full-screen overlay ─────────────────────────────── */

  return (
    <>
      <Fade in={open} timeout={250}>
        <Box sx={{
          position:'fixed', top:0, left:0, right:0, bottom:0,
          bgcolor:'background.default',
          zIndex: OVERLAY_ZINDEX,          // 1301
          display: open ? 'flex' : 'none',
          flexDirection:'column',
        }}>
          {/* Header */}
          <Box sx={{ height:80, flexShrink:0, borderBottom:1, borderColor:'divider', display:'flex', alignItems:'center', justifyContent:'space-between', px:4, bgcolor:'background.paper' }}>
            <Box sx={{ display:'flex', alignItems:'center', gap:2 }}>
              <Box sx={{ width:48, height:48, borderRadius:2.5, background:'linear-gradient(135deg,#4facfe 0%,#00f2fe 100%)', display:'flex', alignItems:'center', justifyContent:'center', boxShadow:'0 4px 16px rgba(79,172,254,0.4)' }}>
                <TrackerIcon sx={{ color:'white', fontSize:26 }}/>
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} lineHeight={1.2}>Create New Tracker</Typography>
                <Typography variant="body2" color="text.secondary">Monitor YouTube, TikTok &amp; Instagram content</Typography>
              </Box>
            </Box>
            <Box sx={{ display:'flex', gap:1.5, alignItems:'center' }}>
              {canSubmit && <Chip icon={<MagicIcon sx={{fontSize:'16px !important'}}/>} label="Ready to create" color="success" size="small" sx={{fontWeight:600}}/>}
              <Button variant="outlined" onClick={handleClose} sx={{borderRadius:2,fontWeight:600}}>Cancel</Button>
              <Button variant="contained" disabled={!canSubmit} onClick={handleCreate}
                sx={{ borderRadius:2, px:4, fontWeight:700, background:'linear-gradient(135deg,#4facfe 0%,#00f2fe 100%)', boxShadow:'0 4px 16px rgba(79,172,254,0.35)', '&:hover':{ background:'linear-gradient(135deg,#3d9ae8 0%,#00d4e0 100%)', boxShadow:'0 6px 20px rgba(79,172,254,0.45)' }, '&.Mui-disabled':{ background:alpha(theme.palette.primary.main,0.2) } }}>
                Create Tracker
              </Button>
            </Box>
          </Box>

          {/* Two-column body */}
          <Box sx={{ flex:1, display:'flex', overflow:'hidden' }}>

            {/* LEFT — metadata + cover */}
            <Box sx={{ width:400, flexShrink:0, borderRight:1, borderColor:'divider', bgcolor:'background.paper', display:'flex', flexDirection:'column', overflowY:'auto' }}>
              <Box
                sx={{ height:220, flexShrink:0, position:'relative', overflow:'hidden', cursor:'pointer',
                  background: trackerPhoto
                    ? `linear-gradient(rgba(0,0,0,0.35),rgba(0,0,0,0.55)),url('${trackerPhoto}') center/cover`
                    : 'linear-gradient(135deg,#0f2027,#203a43,#2c5364)',
                  display:'flex', alignItems:'flex-end',
                }}
                onClick={() => setUploadDialogOpen(true)}
              >
                <Box sx={{ p:3, width:'100%', background:'linear-gradient(transparent,rgba(0,0,0,0.7))' }}>
                  <Typography variant="h6" sx={{ color:'white', fontWeight:700, textShadow:'0 1px 4px rgba(0,0,0,0.5)', mb:0.5 }}>{name||'Tracker Name'}</Typography>
                  <Box sx={{ display:'flex', alignItems:'center', gap:0.75 }}>
                    <Chip label={`${validLinks.length} link${validLinks.length!==1?'s':''} tracked`} size="small" sx={{ bgcolor:'rgba(255,255,255,0.2)', color:'white', fontWeight:600, backdropFilter:'blur(8px)' }}/>
                    {autoNamed && <Chip icon={<AutoIcon sx={{fontSize:'12px !important',color:'white !important'}}/>} label="auto-named" size="small" sx={{ bgcolor:'rgba(79,172,254,0.4)', color:'white', fontWeight:600, backdropFilter:'blur(8px)', fontSize:'0.62rem', height:20 }}/>}
                  </Box>
                </Box>
                <Button size="small"
                  startIcon={trackerPhoto ? <EditIcon fontSize="small"/> : <PhotoIcon fontSize="small"/>}
                  onClick={e => { e.stopPropagation(); setUploadDialogOpen(true); }}
                  sx={{ position:'absolute', top:12, right:12, bgcolor:'rgba(0,0,0,0.5)', color:'white', backdropFilter:'blur(8px)', borderRadius:2, '&:hover':{ bgcolor:'rgba(0,0,0,0.7)' }, fontSize:'0.75rem' }}>
                  {trackerPhoto ? 'Change' : 'Add Photo'}
                </Button>
              </Box>

              <Box sx={{ p:3, flex:1 }}>
                <Stack spacing={3}>
                  <TextField fullWidth label="Tracker Name" value={name} required
                    onChange={e => { setName(e.target.value); setNameTouched(true); }}
                    helperText={autoNamed ? '✨ Auto-suggested from your first link — edit freely' : 'Give your tracker a clear, recognizable name'}
                    FormHelperTextProps={{ sx:{ color:autoNamed?'info.main':undefined, fontWeight:autoNamed?600:undefined } }}
                    InputProps={{
                      sx: { borderRadius:2, '&.Mui-focused .MuiOutlinedInput-notchedOutline':{ borderColor:'#4facfe' } },
                      endAdornment: autoNamed
                        ? <Tooltip title="Auto-suggested from your first link" arrow><AutoIcon sx={{ fontSize:18, color:'info.main', cursor:'default' }}/></Tooltip>
                        : undefined,
                    }}/>
                  <TextField fullWidth label="Description" value={description} onChange={e=>setDescription(e.target.value)} multiline rows={3}
                    helperText="What content or campaign are you tracking?" InputProps={{sx:{borderRadius:2}}}/>
                  <Box>
                    <TextField fullWidth type="number" label="Notification Frequency" value={freq} onChange={handleFreq} inputProps={{min:1}} error={parseInt(freq)<2}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">days</InputAdornment>,
                        startAdornment: <InputAdornment position="start"><NotifIcon fontSize="small" color={parseInt(freq)<2?'error':'action'}/></InputAdornment>,
                        sx: { borderRadius:2 },
                      }}
                      helperText={parseInt(freq)>1?`Email update every ${freq} days`:'Daily notifications require a Blitz Campaign'}/>
                    {parseInt(freq)<2 && (
                      <Alert severity="info" sx={{ mt:1.5, borderRadius:2, fontSize:'0.8rem' }}>
                        For daily tracking, launch a <strong>Blitz Campaign</strong> instead.
                      </Alert>
                    )}
                  </Box>
                </Stack>
              </Box>
            </Box>

            {/* RIGHT — links */}
            <Box sx={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
              <Box sx={{ px:4, py:2.5, borderBottom:1, borderColor:'divider', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
                <Box>
                  <Typography variant="h6" fontWeight={700}>Tracked Links</Typography>
                  <Typography variant="body2" color="text.secondary">YouTube, TikTok &amp; Instagram — up to {MAX_LINKS}</Typography>
                </Box>
                <Box sx={{ display:'flex', alignItems:'center', gap:2 }}>
                  <Box sx={{ width:120 }}>
                    <Box sx={{ display:'flex', justifyContent:'space-between', mb:0.5 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>{validLinks.length} valid</Typography>
                      <Typography variant="caption" color="text.disabled">/ {MAX_LINKS}</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={progress}
                      sx={{ height:6, borderRadius:3, bgcolor:alpha(theme.palette.primary.main,0.12), '& .MuiLinearProgress-bar':{ borderRadius:3, background:'linear-gradient(90deg,#4facfe,#00f2fe)' } }}/>
                  </Box>
                  <Button variant="outlined" size="small" startIcon={<TableChartIcon/>} onClick={() => setCSVModalOpen(true)}
                    sx={{ borderRadius:2, fontWeight:600, borderColor:alpha(theme.palette.info.main,0.5), color:'info.main', '&:hover':{ borderColor:'info.main', bgcolor:alpha(theme.palette.info.main,0.04) } }}>
                    Import CSV
                  </Button>
                  <Button variant="outlined" startIcon={<AddIcon/>} disabled={links.length>=MAX_LINKS} onClick={() => setLinks(p=>[...p,''])} sx={{borderRadius:2,fontWeight:600}}>
                    Add Link
                  </Button>
                </Box>
              </Box>

              <Box sx={{ flex:1, overflowY:'auto', p:4 }}>
                <Stack spacing={2}>
                  {links.map((link,i)=>{
                    const hasVal=!!link, ok=isValidTrackerUrl(link)||!link, plat=detectPlatform(link);
                    return (
                      <Paper key={i} sx={{
                        p:3, borderRadius:3, border:'1.5px solid',
                        borderColor:!ok&&hasVal?'error.main':hasVal&&ok?'success.main':alpha(theme.palette.divider,0.8),
                        transition:'all 0.25s', '&:hover':{ borderColor:!ok&&hasVal?'error.dark':'primary.main', boxShadow:3 },
                      }}>
                        <Box sx={{ display:'flex', gap:2, alignItems:'flex-start' }}>
                          <PlatformAvatar url={link} index={i}/>
                          <Box sx={{ flex:1 }}>
                            <TextField fullWidth label={`Link ${i+1}`} value={link} required onChange={e=>handleLink(e,i)} error={!ok&&hasVal}
                              helperText={!ok&&hasVal?'Please enter a valid YouTube, TikTok, or Instagram URL':hasVal&&ok?`Platform: ${plat==='youtube'?'▶ YouTube':plat==='tiktok'?'♪ TikTok':'📸 Instagram'}`:'Paste a YouTube, TikTok, or Instagram URL'}
                              placeholder="https://youtube.com/... or tiktok.com/... or instagram.com/..."
                              InputProps={{ sx:{borderRadius:2}, startAdornment:<InputAdornment position="start"><LinkIcon fontSize="small" color="action"/></InputAdornment> }}
                              FormHelperTextProps={{ sx:{ color:hasVal&&ok?'success.main':undefined, fontWeight:hasVal&&ok?600:undefined } }}/>
                          </Box>
                          <Tooltip title={links.length<=1?'Need at least one link':'Remove this link'} arrow>
                            <span>
                              <IconButton disabled={links.length<=1} onClick={()=>setLinks(p=>p.filter((_,idx)=>idx!==i))}
                                sx={{ mt:0.5, color:'error.main', '&:hover':{ bgcolor:alpha(theme.palette.error.main,0.1) }, '&.Mui-disabled':{ color:alpha(theme.palette.text.disabled,0.4) } }}>
                                <DeleteIcon/>
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Box>
                      </Paper>
                    );
                  })}

                  {links.length<MAX_LINKS && (
                    <Paper onClick={() => setLinks(p=>[...p,''])} sx={{
                      p:3, borderRadius:3, border:'2px dashed', borderColor:alpha(theme.palette.divider,0.6),
                      display:'flex', alignItems:'center', justifyContent:'center', gap:2,
                      cursor:'pointer', transition:'all 0.25s',
                      '&:hover':{ borderColor:'#4facfe', bgcolor:alpha('#4facfe',0.04), transform:'scale(1.01)' },
                    }}>
                      <AddIcon color="action"/>
                      <Typography variant="body2" color="text.secondary">Add another link</Typography>
                    </Paper>
                  )}

                  <Paper elevation={0} sx={{ p:2, borderRadius:2.5, bgcolor:alpha(theme.palette.info.main,0.05), border:`1px solid ${alpha(theme.palette.info.main,0.15)}` }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display:'block', mb:1, textTransform:'uppercase', letterSpacing:'0.06em', fontSize:'0.65rem' }}>Supported Platforms</Typography>
                    <Box sx={{ display:'flex', gap:2, flexWrap:'wrap' }}>
                      {[
                        { label:'YouTube',   icon:<YouTubeIcon   sx={{fontSize:14,color:'white'}}/>, bg:'#ff0000' },
                        { label:'TikTok',    icon:<TikTokIcon    sx={{fontSize:14,color:'white'}}/>, bg:'#010101' },
                        { label:'Instagram', icon:<InstagramIcon sx={{fontSize:14,color:'white'}}/>, bg:'linear-gradient(135deg,#833ab4,#fd1d1d,#fcb045)' },
                      ].map(({label,icon,bg})=>(
                        <Box key={label} sx={{ display:'flex', alignItems:'center', gap:0.75 }}>
                          <Avatar sx={{ width:24, height:24, background:bg }}>{icon}</Avatar>
                          <Typography variant="caption" fontWeight={600}>{label}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                </Stack>
              </Box>
            </Box>
          </Box>
        </Box>
      </Fade>

      {/*
        Both modals rendered OUTSIDE the overlay Box.
        LinkCSVModal uses style={{ zIndex: DIALOG_ZINDEX }} (= 1402, an inline style).
        UploadFileDialog uses its own internal z-index fix (same value).
        Inline styles beat any CSS class MUI applies internally, so these
        will always appear above the overlay (OVERLAY_ZINDEX = 1301).
      */}
      <UploadFileDialog
        open={uploadDialogOpen}
        onClose={() => setUploadDialogOpen(false)}
        onUploadSuccess={handlePhotoUploadSuccess}
      />
      <LinkCSVModal
        open={csvModalOpen}
        onClose={() => setCSVModalOpen(false)}
        onImport={handleCSVImport}
      />
    </>
  );
}